import { useCallback, useEffect, useRef, useState } from 'react';
import { LogOut, Siren as RescueIcon } from 'lucide-react';
import { LoginScreen } from './components/LoginScreen';
import { PanicButton } from './components/PanicButton';
import { StatusPanel } from './components/StatusPanel';
import type { PanicState } from './components/StatusPanel';
import { PANIC_TIMEOUT_SECONDS, checkSession, triggerPanic } from './lib/api';
import { useGoogleAuth } from './lib/auth';
import { hostnameWarning } from './lib/origin';
import { productForHostname } from './lib/products';
// The Function truncates to this same constant, so the counter cannot promise
// room the alert will not carry.
import { MAX_NOTE_CHARS } from './lib/limits';

export default function App() {
  const auth = useGoogleAuth();
  const [note, setNote] = useState('');
  const [state, setState] = useState<PanicState>({ kind: 'ready' });
  const inFlightRef = useRef(false);

  // The hold that fires the alert captures its callbacks when the finger goes
  // down. Reading the note through a ref means an edit made during those two
  // seconds still reaches OpsGenie.
  const noteRef = useRef(note);
  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  // fire() is memoised on `credential` alone so the hold gesture keeps a stable
  // callback, so it reads the current state through a ref rather than closing over
  // a stale one.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const credential = auth.credential;

  const fire = useCallback(async () => {
    // A second call cannot happen through the UI, the button is disabled while
    // sending. The guard is here because paging twice is the failure mode that
    // costs someone their sleep.
    if (inFlightRef.current) return;

    if (credential === null) {
      setState({
        kind: 'error',
        message: 'Tu sesión ya no está disponible, así que no se pudo enviar la alerta.',
        recover: 'sign_in_again',
      });
      return;
    }

    inFlightRef.current = true;
    setState({ kind: 'sending' });

    const trimmed = noteRef.current.trim();
    // A press made while the cooldown panel is on screen is the sender saying "I
    // saw that, send it anyway". The first press can never carry this, so the
    // warning is always shown before a bypass is possible.
    const override = stateRef.current.kind === 'cooldown';
    const result = await triggerPanic({
      credential,
      note: trimmed.length > 0 ? trimmed : undefined,
      override,
    });
    inFlightRef.current = false;

    switch (result.kind) {
      case 'ok':
        setState({ kind: 'sent', alertId: result.alertId, responderTeams: result.responderTeams });
        break;

      case 'cooldown':
        setState({
          kind: 'cooldown',
          until: Date.now() + result.retryAfterSeconds * 1000,
          alertId: result.alertId,
        });
        break;

      case 'unauthorized':
        setState({
          kind: 'error',
          message:
            result.message ??
            'Google no confirmó este inicio de sesión, que normalmente significa que la sesión expiró. No se creó ninguna alerta.',
          recover: 'sign_in_again',
        });
        break;

      case 'forbidden':
        setState({
          kind: 'error',
          message:
            result.message ??
            'Esta cuenta no puede llamar a guardia. Iniciá sesión con tu cuenta corporativa de Vertex.',
          recover: 'sign_in_again',
        });
        break;

      case 'upstream_error':
        setState({
          kind: 'error',
          message:
            result.status >= 500
              ? `El servicio de avisos falló con HTTP ${result.status}${result.message === null ? '' : `: ${result.message}`}. No se creó ninguna alerta.`
              : `La solicitud fue rechazada con HTTP ${result.status}${result.message === null ? '' : `: ${result.message}`}.`,
          recover: 'retry',
        });
        break;

      case 'network_error':
        setState({
          kind: 'error',
          message: result.timedOut
            ? `La solicitud se canceló después de ${PANIC_TIMEOUT_SECONDS} segundos sin respuesta, así que no queda claro si la alerta se creó. Volver a intentar es seguro, los avisos duplicados dentro de cinco minutos se agrupan en una sola alerta.`
            : 'La solicitud nunca llegó al servidor. Revisá la conexión de red.',
          recover: 'retry',
        });
        break;
    }
  }, [credential]);

  const handleChargeStart = useCallback(() => {
    // Charging must not wipe the result of the previous attempt until the new one
    // actually starts, hence only these two states are replaced.
    setState((current) =>
      current.kind === 'ready' || current.kind === 'error' ? { kind: 'charging' } : current
    );
  }, []);

  const handleChargeCancel = useCallback(() => {
    setState((current) => (current.kind === 'charging' ? { kind: 'ready' } : current));
  }, []);

  const handleReset = useCallback(() => {
    setState({ kind: 'ready' });
  }, []);

  const handleCooldownExpired = useCallback(() => {
    setState((current) => (current.kind === 'cooldown' ? { kind: 'ready' } : current));
  }, []);

  const handleSignOut = useCallback(
    (notice?: string) => {
      setState({ kind: 'ready' });
      setNote('');
      auth.signOut(notice);
    },
    [auth]
  );

  const handleSignInAgain = useCallback(() => {
    handleSignOut('Iniciá sesión de nuevo y mantené presionado el botón para llamar a guardia.');
  }, [handleSignOut]);

  const user = auth.user;

  // Resolved at render from the address bar, matching how the Pages Function
  // resolves it from the request Host. Cosmetic here, the Function decides what
  // the alert actually says.
  const product = productForHostname(window.location.hostname).product;

  // Whether this account may page, answered by the server. The allowlist is server
  // side only (functions/lib/domains.ts), so the browser cannot work it out and
  // must not be able to: that list is a phishing target.
  //
  // 'unknown' means the check itself failed, and it deliberately shows the button.
  // POST /api/panic re-verifies everything, so the cost of being wrong here is a
  // 403 on press, whereas hiding the button because a check timed out would remove
  // the panic button during the kind of incident it exists for.
  const [access, setAccess] = useState<'checking' | 'allowed' | 'denied' | 'unknown'>('checking');

  useEffect(() => {
    if (credential === null) {
      setAccess('checking');
      return;
    }
    let current = true;
    setAccess('checking');
    void checkSession(credential).then((result) => {
      if (!current) return;
      // An 'unauthorized' answer means the token is bad, which the panic call will
      // report properly. Treat it as unknown rather than denied: "your account is
      // not allowed" would be the wrong explanation for an expired session.
      setAccess(
        result.kind === 'allowed' ? 'allowed' : result.kind === 'denied' ? 'denied' : 'unknown'
      );
    });
    return () => {
      current = false;
    };
  }, [credential]);

  if (auth.status !== 'signed_in' || user === null || access === 'denied') {
    return (
      <div className="flex min-h-[var(--h-dvh)] flex-col">
        <LoginScreen
          buttonRef={auth.buttonRef}
          loading={auth.status === 'loading'}
          buttonReady={auth.buttonReady}
          blockedReason={auth.blockedReason}
          notice={auth.notice}
          // Computed at render, not once at module load: the value depends on the
          // address in the URL bar, which is the thing being checked.
          hostnameWarning={hostnameWarning(window.location.hostname)}
          rejectedEmail={access === 'denied' && user !== null ? user.email : null}
          onUseAnotherAccount={() => handleSignOut()}
        />
      </div>
    );
  }

  let blockedReason: string | null = null;
  if (state.kind === 'sent') {
    blockedReason = 'Guardia ya fue notificada, no hay nada más que enviar.';
  }
  // Deliberately nothing for 'cooldown'. The button stays usable, because holding
  // it again is how a sender bypasses the cooldown, and a disabled button would
  // leave them no way to page about something the open alert does not cover.

  const noteRemaining = MAX_NOTE_CHARS - note.length;

  return (
    <div className="flex min-h-[var(--h-dvh)] flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 motion-safe:animate-fade-in sm:px-6">
        {/* The product is derived from the hostname, the same way the Function
            derives it, so the header can never claim a different system than the
            one the alert will name. */}
        <div className="flex min-w-0 items-center gap-2">
          <RescueIcon className="size-5 shrink-0 text-primary" aria-hidden="true" />
          <h1 className="truncate text-sm font-extrabold tracking-tight">
            Vertex Rescue
            <span className="ml-1.5 font-semibold text-muted-foreground">{product.name}</span>
          </h1>
        </div>

        <div className="flex min-w-0 items-center gap-2">
          {user.picture === undefined ? (
            <span
              aria-hidden="true"
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-bold text-secondary-foreground"
            >
              {user.name.slice(0, 1).toUpperCase()}
            </span>
          ) : (
            <img
              src={user.picture}
              alt=""
              width={32}
              height={32}
              // Google's avatar host rejects requests that leak our referrer.
              referrerPolicy="no-referrer"
              className="size-8 shrink-0 rounded-full bg-secondary object-cover"
            />
          )}
          <span className="hidden min-w-0 flex-col leading-tight sm:flex">
            <span className="truncate text-sm font-semibold">{user.name}</span>
            <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          </span>
          <button
            type="button"
            onClick={() => handleSignOut()}
            className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-input px-3 text-sm font-semibold transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2"
          >
            <LogOut className="size-4" aria-hidden="true" />
            Cerrar sesión
          </button>
        </div>
      </header>

      {/* On a narrow screen the email is hidden in the header, so it is repeated
          here: the account is what ends up on the alert, and the sender should be
          able to see it before paging anyone. */}
      <p className="truncate border-b border-border bg-secondary/40 px-4 py-2 text-xs text-muted-foreground sm:hidden">
        Avisando como <span className="font-semibold text-foreground">{user.email}</span>
      </p>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center gap-6 px-4 py-8 motion-safe:animate-fade-in-up sm:gap-8 sm:py-12">
        <div className="w-full">
          <label htmlFor="panic-note" className="block text-sm font-semibold">
            Nota <span className="font-normal text-muted-foreground">(opcional)</span>
          </label>
          <input
            id="panic-note"
            name="note"
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={MAX_NOTE_CHARS}
            placeholder="¿qué está roto?"
            autoComplete="off"
            autoCapitalize="sentences"
            spellCheck={false}
            enterKeyHint="done"
            aria-describedby="panic-note-help"
            className="mt-1.5 h-12 w-full rounded-lg border border-input bg-card px-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2"
          />
          <p id="panic-note-help" className="mt-1.5 text-xs text-muted-foreground">
            Va directo a la alerta que el ingeniero lee primero.
            {noteRemaining <= 40 && ` Quedan ${noteRemaining} caracteres.`}
          </p>
        </div>

        <PanicButton
          onFire={fire}
          onChargeStart={handleChargeStart}
          onChargeCancel={handleChargeCancel}
          busy={state.kind === 'sending'}
          blockedReason={blockedReason}
        />

        <StatusPanel
          state={state}
          onRetry={fire}
          onSignInAgain={handleSignInAgain}
          onReset={handleReset}
          onCooldownExpired={handleCooldownExpired}
        />
      </main>
    </div>
  );
}
